import { useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatCurrency } from "@/lib/formatters";

const STATE_GRID = [
  { abbr: "AK", row: 0, col: 0 }, { abbr: "ME", row: 0, col: 10 },
  { abbr: "WA", row: 1, col: 0 }, { abbr: "MT", row: 1, col: 1 }, { abbr: "ND", row: 1, col: 2 },
  { abbr: "MN", row: 1, col: 3 }, { abbr: "WI", row: 1, col: 5 }, { abbr: "MI", row: 1, col: 7 },
  { abbr: "NY", row: 1, col: 8 }, { abbr: "VT", row: 1, col: 9 }, { abbr: "NH", row: 1, col: 10 },
  { abbr: "OR", row: 2, col: 0 }, { abbr: "ID", row: 2, col: 1 }, { abbr: "SD", row: 2, col: 2 },
  { abbr: "NE", row: 2, col: 3 }, { abbr: "IA", row: 2, col: 4 }, { abbr: "IL", row: 2, col: 5 },
  { abbr: "IN", row: 2, col: 6 }, { abbr: "OH", row: 2, col: 7 }, { abbr: "PA", row: 2, col: 8 },
  { abbr: "MA", row: 2, col: 9 }, { abbr: "CT", row: 2, col: 10 },
  { abbr: "NV", row: 3, col: 0 }, { abbr: "WY", row: 3, col: 1 }, { abbr: "CO", row: 3, col: 2 },
  { abbr: "KS", row: 3, col: 3 }, { abbr: "MO", row: 3, col: 4 }, { abbr: "KY", row: 3, col: 5 },
  { abbr: "WV", row: 3, col: 6 }, { abbr: "VA", row: 3, col: 7 }, { abbr: "NJ", row: 3, col: 8 },
  { abbr: "RI", row: 3, col: 9 }, { abbr: "DE", row: 3, col: 10 },
  { abbr: "CA", row: 4, col: 0 }, { abbr: "UT", row: 4, col: 1 }, { abbr: "NM", row: 4, col: 2 },
  { abbr: "OK", row: 4, col: 3 }, { abbr: "AR", row: 4, col: 4 }, { abbr: "TN", row: 4, col: 5 },
  { abbr: "NC", row: 4, col: 6 }, { abbr: "SC", row: 4, col: 7 }, { abbr: "MD", row: 4, col: 8 },
  { abbr: "DC", row: 4, col: 9 },
  { abbr: "AZ", row: 5, col: 0 }, { abbr: "TX", row: 5, col: 3 }, { abbr: "LA", row: 5, col: 4 },
  { abbr: "MS", row: 5, col: 5 }, { abbr: "AL", row: 5, col: 6 }, { abbr: "GA", row: 5, col: 7 },
  { abbr: "FL", row: 6, col: 7 }, { abbr: "HI", row: 6, col: 0 },
];

function getIntensity(value, maxVal) {
  if (!value || !maxVal) return 0;
  return Math.min(value / maxVal, 1);
}

export default function USMapGrid({ data = [], onStateClick, className = "" }) {
  const [hovered, setHovered] = useState(null);

  const dataMap = {};
  data.forEach((d) => { dataMap[d.state_code] = d; });
  const maxVal = Math.max(...data.map((d) => d.value || 0), 1);

  return (
    <TooltipProvider delayDuration={0}>
      <div className={`grid gap-1 ${className}`} style={{ gridTemplateColumns: "repeat(11, 1fr)", gridTemplateRows: "repeat(7, 1fr)" }} data-testid="us-map-grid">
        {STATE_GRID.map((state) => {
          const stateData = dataMap[state.abbr];
          const intensity = stateData ? getIntensity(stateData.value, maxVal) : 0;
          const hasData = !!stateData;

          return (
            <Tooltip key={state.abbr}>
              <TooltipTrigger asChild>
                <button
                  className="aspect-square rounded-sm flex items-center justify-center text-[10px] font-data font-medium transition-all duration-150 hover:scale-110 hover:z-10 border"
                  style={{
                    gridRow: state.row + 1,
                    gridColumn: state.col + 1,
                    backgroundColor: hasData
                      ? `rgba(59, 130, 246, ${0.15 + intensity * 0.75})`
                      : "hsl(215 28% 17%)",
                    borderColor: hasData ? `rgba(59, 130, 246, ${0.3 + intensity * 0.5})` : "hsl(218 23% 23%)",
                    color: hasData ? "#F3F4F6" : "#6B7280",
                  }}
                  onClick={() => stateData && onStateClick?.(stateData)}
                  onMouseEnter={() => setHovered(state.abbr)}
                  onMouseLeave={() => setHovered(null)}
                  data-testid={`map-state-${state.abbr}`}
                >
                  {state.abbr}
                </button>
              </TooltipTrigger>
              {stateData && (
                <TooltipContent className="bg-popover border-border p-3 space-y-1" side="top">
                  <p className="font-semibold text-sm">{stateData.state}</p>
                  <div className="text-xs space-y-0.5 font-data text-muted-foreground">
                    <p>Value: <span className="text-foreground">{formatCurrency(stateData.value)}</span></p>
                    <p>IRR: <span className="text-foreground">{stateData.irr}%</span></p>
                    <p>Properties: <span className="text-foreground">{stateData.num_properties}</span></p>
                  </div>
                </TooltipContent>
              )}
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
